import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import _ from 'lodash';

interface SuggestedTextField {
  original: string;
  modified: string;
}

type RLHFHackStore = {
  initialized?: boolean;
  setInitialized: (
    forceUseRlhfHackStore: boolean,
    params: any,
    annotations: any,
    setAnnotations: any,
    assignmentId?: string,
    randomizeReviewOptions?: boolean,
  ) => void;
  // first item is the original text; the remaining ones are revised by ChatGPT
  suggestedFields: SuggestedTextField[];
  suggestedFieldId?: string;
  updateSuggestedField: (modifiedText: string) => void;
  activeSuggestedFieldIndex: number;
  setActiveSuggestedFieldIndex: (index: number) => void;
  setFinalResponse: any;
  reviewOrderMapping?: { to: number; from: number }[];
};

export const useRLHFHackStore = create<RLHFHackStore>()(
  immer(set => ({
    initialized: undefined,
    setInitialized: (
      forceUseRlhfHackStore: boolean,
      params: any,
      annotations: any,
      setAnnotations: any,
      assignmentId?: string,
      randomizeReviewOptions?: boolean,
    ) => {
      set(state => {
        const isFair = _.get(params, 'isFair', false);
        const isAirdale = _.get(params, 'isAirdale', false);
        const suggestedFieldId: string = isAirdale
          ? _.get(params, `fields[${params.fields.length - 1}].field_id`, 'Continuation passage')
          : 'final_fix_modification';
        const suggestedTexts = _.get(
          params,
          `metadata.suggested_responses_${suggestedFieldId}`,
          [],
        ) as string[];
        const originalText = _.get(annotations, `${suggestedFieldId}.response[0]`, '') as string;

        const isReviewSubtask =
          'option_quality_1' in annotations &&
          'option_quality_2' in annotations &&
          'option_quality_3' in annotations;

        const isFinalFix =
          _.get(annotations, `${suggestedFieldId}.response`, []).length && suggestedTexts.length;

        if (
          !(
            ((isFair || isAirdale || suggestedTexts.length > 0) &&
              (isFinalFix || (isReviewSubtask && randomizeReviewOptions))) ||
            forceUseRlhfHackStore
          )
        ) {
          return;
        }

        const attachments = params.attachments;
        const fields = params.fields;

        if (attachments.length > 1 && isReviewSubtask) {
          return;
        } else if (isReviewSubtask) {
          const content: string = attachments[0].content;

          const optionOneLoc = content.indexOf('**Option 1**');
          const optionTwoLoc = content.indexOf('**Option 2**');
          const optionThreeLoc = content.indexOf('**Option 3**');

          if ([optionOneLoc, optionTwoLoc, optionThreeLoc].includes(-1)) {
            return;
          }
          const title = content.slice(0, optionOneLoc);

          const cachedMapping = assignmentId
            ? localStorage.getItem(`shuffle-mapping-${assignmentId}`)
            : undefined;

          let mapping: number[] = [];

          if (cachedMapping) {
            mapping = JSON.parse(cachedMapping);
          } else {
            mapping = _.shuffle([1, 2, 3]);

            if (assignmentId) {
              try {
                localStorage.setItem(`shuffle-mapping-${assignmentId}`, JSON.stringify(mapping));
              } catch {
                // prevent erroring-out when out of localstorage
              }
            }
          }

          state.reviewOrderMapping = mapping.map((m, idx) => ({ to: m, from: idx + 1 }));

          const text = [
            {
              idx: mapping[0],
              content: content
                .slice(optionOneLoc, optionTwoLoc)
                .replace('**Option 1**', `**Option ${mapping[0]}**`),
            },
            {
              idx: mapping[1],
              content: content
                .slice(optionTwoLoc, optionThreeLoc)
                .replace('**Option 2**', `**Option ${mapping[1]}**`),
            },
            {
              idx: mapping[2],
              content: content
                .slice(optionThreeLoc)
                .replace('**Option 3**', `**Option ${mapping[2]}**`),
            },
          ].sort((a, b) => a.idx - b.idx);
          const newContent = title + text.map(t => t.content).join('');

          const reverseMapping = [...state.reviewOrderMapping]
            .sort((a, b) => a.to - b.to)
            .map(s => s.from);

          const newFields = fields.map((f: any) => {
            const newField = { ...f };
            const option = [1, 2, 3].find(i => newField.field_id === `option_quality_${i}`);
            if (option) {
              newField.field_id = newField.field_id.replace(
                `${option}`,
                `${reverseMapping[option - 1]}`,
              );
            }

            return newField;
          });

          state.initialized = true;
          attachments[0].content = newContent;
          params.fields = newFields;
          return;
        }

        state.initialized = true;
        state.suggestedFields = [
          { original: originalText, modified: originalText },
          ...suggestedTexts.map(suggestedText => ({
            original: suggestedText,
            modified: suggestedText,
          })),
        ];
        state.suggestedFieldId = suggestedFieldId;
        state.setFinalResponse = (text: string, activeIndex: number) => {
          setAnnotations((currentAnnotations: any) => ({
            ...currentAnnotations,
            [suggestedFieldId]: {
              field_id: suggestedFieldId,
              response: [text],
              type: 'text',
            },
            active_suggested_response_index: {
              field_id: 'active_suggested_response_index',
              response: [activeIndex.toString()],
              type: 'number',
            },
          }));
        };
        state.reviewOrderMapping = [];
      });
    },
    suggestedFields: [],
    updateSuggestedField: (modifiedText: string) => {
      set(state => {
        if (state.initialized) {
          state.suggestedFields[state.activeSuggestedFieldIndex].modified = modifiedText;
          state.setFinalResponse(modifiedText, state.activeSuggestedFieldIndex);
        }
      });
    },
    activeSuggestedFieldIndex: 0,
    setActiveSuggestedFieldIndex: (activeSuggestedFieldIndex: number) => {
      return set(state => {
        if (state.initialized) {
          state.activeSuggestedFieldIndex = activeSuggestedFieldIndex;
          state.setFinalResponse(
            state.suggestedFields[activeSuggestedFieldIndex].modified,
            state.activeSuggestedFieldIndex,
          );
        }
      });
    },
    suggestedFieldId: undefined,
    setFinalResponse: undefined,
    reviewOrderMapping: undefined,
  })),
);
